import { Alert, Button, Field, Input, Modal, Segmented, Switch } from "../../ds";
import type { NetworkStatus, WifiStatus } from "../../lib/api";

// „Uplink hinzufügen“ — ein Formular für das, was auf dem Router vier Sections
// in drei Configs sind: Interface, Firewall-Zone, `sta`-Netz, mwan3. Der Server
// schreibt sie in EINEM `uci_set` auf Probe (docs/device-management.md).

export interface UplinkForm {
  kind: "ethernet" | "wifi";
  name: string;
  device: string;
  proto: "dhcp" | "static";
  ipaddr: string;
  netmask: string;
  gateway: string;
  dns: string;
  dualstack: boolean;
  metric: string;
  zone: string;
  radio: string;
  ssid: string;
  encryption: string;
  key: string;
  bssid: string;
  mwan: boolean;
  mwanMetric: string;
  mwanWeight: string;
  mwanTrack: string;
  mwanPolicies: string;
}

const ENCRYPTIONS = ["psk2", "sae-mixed", "sae", "psk-mixed", "none"];

export function emptyUplinkForm(): UplinkForm {
  return {
    kind: "ethernet",
    name: "",
    device: "",
    proto: "dhcp",
    ipaddr: "",
    netmask: "255.255.255.0",
    gateway: "",
    dns: "",
    dualstack: true,
    metric: "",
    zone: "wan",
    radio: "",
    ssid: "",
    encryption: "psk2",
    key: "",
    bssid: "",
    mwan: false,
    mwanMetric: "10",
    mwanWeight: "1",
    mwanTrack: "1.1.1.1, 9.9.9.9",
    mwanPolicies: "",
  };
}

const list = (text: string) =>
  text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/** Formular → `uplink_add`-Params. Was leer ist, geht nicht mit — der Server
 *  setzt dann seine Defaults (`proto dhcp`, Zone `wan`). */
export function toUplinkParams(form: UplinkForm): Record<string, unknown> {
  const params: Record<string, unknown> = {
    name: form.name.trim(),
    kind: form.kind,
    proto: form.proto,
    zone: form.zone.trim() || "wan",
  };
  if (form.kind === "ethernet") params.device = form.device.trim();
  else {
    params.radio = form.radio;
    params.ssid = form.ssid;
    params.encryption = form.encryption;
    if (form.encryption !== "none" && form.key) params.key = form.key;
    if (form.bssid.trim()) params.bssid = form.bssid.trim();
  }
  if (form.proto === "static") {
    params.ipaddr = form.ipaddr.trim();
    params.netmask = form.netmask.trim();
    if (form.gateway.trim()) params.gateway = form.gateway.trim();
    if (list(form.dns).length) params.dns = list(form.dns);
  } else if (form.dualstack) {
    params.dualstack = true;
  }
  if (form.metric.trim()) params.metric = Number(form.metric);
  if (form.mwan) {
    params.mwan = {
      enabled: true,
      metric: Number(form.mwanMetric) || 10,
      weight: Number(form.mwanWeight) || 1,
      ...(list(form.mwanTrack).length ? { track_ips: list(form.mwanTrack) } : {}),
      ...(list(form.mwanPolicies).length ? { policies: list(form.mwanPolicies) } : {}),
    };
  }
  return params;
}

/** Was der Router an Geräten kennt — Ports seiner Bridges und die Geräte seiner
 *  Interfaces. Bewusst aus dem vorhandenen Netzwerk-Stand statt aus einem
 *  zusätzlichen ubus-Aufruf: ein zweiter Frame je Aufruf wäre für eine
 *  Auswahlliste nicht zu rechtfertigen (Datensparsamkeit). */
export function knownDevices(net: NetworkStatus | null): string[] {
  const seen = new Set<string>();
  for (const dev of net?.devices ?? []) {
    if (dev.name) seen.add(dev.name);
    for (const port of dev.ports ?? []) seen.add(port);
  }
  for (const iface of net?.interfaces ?? []) if (iface.device) seen.add(iface.device);
  return [...seen].sort();
}

function EthernetFields({
  form,
  devices,
  onChange,
}: {
  form: UplinkForm;
  devices: string[];
  onChange: (form: UplinkForm) => void;
}) {
  return (
    <Field label="Gerät" helperText="Port aus der Liste oder frei tippen" htmlFor="uplink-device">
      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          id="uplink-device"
          className="ds-input sm:w-1/2"
          value={devices.includes(form.device) ? form.device : ""}
          onChange={(e) => onChange({ ...form, device: e.target.value })}
        >
          <option value="">— bekannte Geräte —</option>
          {devices.map((dev) => (
            <option key={dev} value={dev}>
              {dev}
            </option>
          ))}
        </select>
        <Input
          label=""
          fieldClassName="sm:w-1/2"
          placeholder="eth1"
          value={form.device}
          onChange={(e) => onChange({ ...form, device: e.target.value })}
        />
      </div>
    </Field>
  );
}

function WifiFields({
  form,
  radios,
  onChange,
}: {
  form: UplinkForm;
  radios: string[];
  onChange: (form: UplinkForm) => void;
}) {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Radio" htmlFor="uplink-radio">
          <select
            id="uplink-radio"
            className="ds-input"
            value={form.radio}
            onChange={(e) => onChange({ ...form, radio: e.target.value })}
          >
            <option value="">— wählen —</option>
            {radios.map((radio) => (
              <option key={radio} value={radio}>
                {radio}
              </option>
            ))}
          </select>
        </Field>
        <Input
          label="SSID"
          helperText="SSID des fremden Netzes"
          value={form.ssid}
          onChange={(e) => onChange({ ...form, ssid: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Verschlüsselung" htmlFor="uplink-enc">
          <select
            id="uplink-enc"
            className="ds-input"
            value={form.encryption}
            onChange={(e) => onChange({ ...form, encryption: e.target.value })}
          >
            {ENCRYPTIONS.map((enc) => (
              <option key={enc} value={enc}>
                {enc}
              </option>
            ))}
          </select>
        </Field>
        <Input
          label="Schlüssel"
          type="password"
          disabled={form.encryption === "none"}
          value={form.key}
          onChange={(e) => onChange({ ...form, key: e.target.value })}
        />
        <Input
          label="BSSID"
          helperText="optional, bindet an einen AP"
          value={form.bssid}
          onChange={(e) => onChange({ ...form, bssid: e.target.value })}
        />
      </div>
    </>
  );
}

function MwanFields({
  form,
  policies,
  onChange,
}: {
  form: UplinkForm;
  policies: string[];
  onChange: (form: UplinkForm) => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line p-3">
      <Switch
        label="Unter mwan3-Aufsicht stellen"
        checked={form.mwan}
        onChange={(v) => onChange({ ...form, mwan: v })}
      />
      {form.mwan && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Metrik"
              helperText="kleiner = zuerst"
              inputMode="numeric"
              value={form.mwanMetric}
              onChange={(e) => onChange({ ...form, mwanMetric: e.target.value })}
            />
            <Input
              label="Gewicht"
              inputMode="numeric"
              value={form.mwanWeight}
              onChange={(e) => onChange({ ...form, mwanWeight: e.target.value })}
            />
            <Input
              label="Prüf-Ziele"
              helperText="kommagetrennt"
              value={form.mwanTrack}
              onChange={(e) => onChange({ ...form, mwanTrack: e.target.value })}
            />
          </div>
          <Input
            label="In diese Policies aufnehmen"
            helperText={
              policies.length
                ? `vorhanden: ${policies.join(", ")}`
                : "keine Policy vorhanden — Member bleibt ungenutzt"
            }
            value={form.mwanPolicies}
            onChange={(e) => onChange({ ...form, mwanPolicies: e.target.value })}
          />
        </>
      )}
    </div>
  );
}

/** Name, Protokoll, Metrik — die drei Felder, die jeder Uplink hat. */
function BasicFields({
  form,
  nameError,
  onChange,
}: {
  form: UplinkForm;
  nameError?: string;
  onChange: (form: UplinkForm) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Input
        label="Name"
        helperText="uci-Interface, z.B. wan2"
        value={form.name}
        error={nameError}
        autoFocus
        onChange={(e) => onChange({ ...form, name: e.target.value })}
      />
      <Field label="Adresse bezieht er" htmlFor="uplink-proto">
        <select
          id="uplink-proto"
          className="ds-input"
          value={form.proto}
          onChange={(e) => onChange({ ...form, proto: e.target.value as UplinkForm["proto"] })}
        >
          <option value="dhcp">automatisch (DHCP)</option>
          <option value="static">fest (statisch)</option>
        </select>
      </Field>
      <Input
        label="Routen-Metrik"
        helperText="kleiner gewinnt"
        inputMode="numeric"
        value={form.metric}
        onChange={(e) => onChange({ ...form, metric: e.target.value })}
      />
    </div>
  );
}

/** Feste Adresse eintippen — oder bei DHCP nur die Frage, ob auch v6. */
function AddressFields({
  form,
  onChange,
}: {
  form: UplinkForm;
  onChange: (form: UplinkForm) => void;
}) {
  if (form.proto !== "static") {
    return (
      <Switch
        label="Auch IPv6 beziehen (eigenes Interface, wie wan6)"
        checked={form.dualstack}
        onChange={(v) => onChange({ ...form, dualstack: v })}
      />
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
      <Input
        label="IP-Adresse"
        value={form.ipaddr}
        onChange={(e) => onChange({ ...form, ipaddr: e.target.value })}
      />
      <Input
        label="Netzmaske"
        value={form.netmask}
        onChange={(e) => onChange({ ...form, netmask: e.target.value })}
      />
      <Input
        label="Gateway"
        value={form.gateway}
        onChange={(e) => onChange({ ...form, gateway: e.target.value })}
      />
      <Input
        label="DNS"
        helperText="kommagetrennt"
        value={form.dns}
        onChange={(e) => onChange({ ...form, dns: e.target.value })}
      />
    </div>
  );
}

/** Was das Formular verlangt, bevor „Auf Probe anlegen“ überhaupt Sinn ergibt. */
function checkForm(form: UplinkForm, taken: Set<string>) {
  const name = form.name.trim();
  const nameError = taken.has(name)
    ? "Ein Interface dieses Namens gibt es schon."
    : /^[A-Za-z0-9_]*$/.test(name)
      ? undefined
      : "Nur Buchstaben, Ziffern und Unterstrich.";
  const ready =
    name !== "" &&
    !nameError &&
    (form.kind === "ethernet"
      ? form.device.trim() !== ""
      : form.radio !== "" && form.ssid.trim() !== "") &&
    (form.proto !== "static" || (form.ipaddr.trim() !== "" && form.netmask.trim() !== ""));
  return { nameError, ready };
}

export function UplinkModal({
  form,
  net,
  wifi,
  busy,
  onChange,
  onClose,
  onSubmit,
}: {
  form: UplinkForm | null;
  net: NetworkStatus | null;
  wifi: WifiStatus | null;
  busy: boolean;
  onChange: (form: UplinkForm) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  if (form === null) return null;

  const radios = (wifi?.radios ?? []).map((r) => r.id);
  const policies = (net?.mwan?.policies ?? []).map((p) => p.id);
  const { nameError, ready } = checkForm(form, new Set((net?.interfaces ?? []).map((i) => i.id)));

  return (
    <Modal
      open
      onClose={onClose}
      title="Uplink hinzufügen"
      subtitle="Interface, Firewall-Zone und — bei WLAN — das Client-Netz entstehen zusammen."
      size="lg"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="check"
            disabled={!ready}
            loading={busy}
            onClick={onSubmit}
          >
            Auf Probe anlegen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="info">
          Auf Probe — ohne Bestätigung Rollback nach 180 s. Bei WLAN reißt der Radio-Reload einen
          vorhandenen STA kurz ab.
        </Alert>

        <Segmented
          options={[
            { value: "ethernet", label: "Ethernet (Kabel)" },
            { value: "wifi", label: "WLAN (Client)" },
          ]}
          value={form.kind}
          onChange={(kind) => onChange({ ...form, kind })}
        />

        <BasicFields form={form} nameError={nameError} onChange={onChange} />

        {form.kind === "ethernet" ? (
          <EthernetFields form={form} devices={knownDevices(net)} onChange={onChange} />
        ) : (
          <WifiFields form={form} radios={radios} onChange={onChange} />
        )}

        <AddressFields form={form} onChange={onChange} />

        <Input
          label="Firewall-Zone"
          helperText="Muss existieren — ohne Zone kein NAT, kein Forward."
          value={form.zone}
          onChange={(e) => onChange({ ...form, zone: e.target.value })}
        />

        {net?.mwan && <MwanFields form={form} policies={policies} onChange={onChange} />}
      </div>
    </Modal>
  );
}
